<template>
  <aside class="progress-column" aria-labelledby="progress-heading">
    <h2 id="progress-heading" class="column-title">Ma progression</h2>

    <p v-if="!learner" class="column-empty">Votre progression est en cours de chargement.</p>

    <template v-else>
      <p class="level-label">{{ learner.levelLabel }}</p>

      <!-- L'avancement est annoncé **en texte** avant d'être dessiné : une barre seule ne dit rien
           à un lecteur d'écran, et la valeur exacte n'est pas déductible de sa longueur. -->
      <p class="points-line">{{ pointsLabel }}</p>
      <div
        class="level-track"
        role="progressbar"
        :aria-valuenow="learner.points"
        :aria-valuemin="learner.levelThreshold || 0"
        :aria-valuemax="learner.nextLevelThreshold || learner.points"
        :aria-valuetext="pointsLabel"
      >
        <div class="level-fill" :style="{ width: levelPercent + '%' }"></div>
      </div>

      <h3 class="distinctions-title">Mes distinctions</h3>
      <p v-if="distinctions.length === 0" class="column-empty">
        Aucune distinction n'est proposée dans votre établissement pour l'instant.
      </p>
      <ul v-else class="distinctions">
        <li v-for="distinction in distinctions" :key="distinction.code" class="distinction">
          <!-- L'icône est décorative : l'état « obtenue » ou « à obtenir » est porté par le texte,
               jamais par elle seule ni par la couleur (RGAA 9.2.1). -->
          <span class="distinction-icon" aria-hidden="true">
            <!-- Une distinction porte un nom Material Design (`mdi-certificate`) ou un emoji selon
                 son origine : interpoler le premier en texte affichait l'identifiant brut. -->
            <v-icon v-if="isIconName(distinction.icon)" aria-hidden="true" small>{{ distinction.icon }}</v-icon>
            <template v-else>{{ distinction.icon }}</template>
          </span>
          <span class="distinction-body">
            <span class="distinction-label">{{ distinction.label }}</span>
            <span class="distinction-state">{{ stateOf(distinction) }}</span>
            <span v-if="distinction.condition" class="distinction-condition">{{ distinction.condition }}</span>
          </span>
        </li>
      </ul>
    </template>
  </aside>
</template>

<script>
/**
 * Colonne de progression du convive.
 *
 * Ne porte **que** la progression (§9.5). Elle rendait auparavant un menu de six entrées en
 * `<li>` non interactifs : ils annonçaient des destinations — « Mes Statistiques », « Classement »,
 * « Paramètres » — sans y mener et sans être atteignables au clavier. Tout déplacement passe
 * désormais par le menu transverse du shell.
 *
 * Aucun badge n'est écrit ici : les six littéraux, dont trois présentés comme obtenus, s'affichaient
 * pour tout convive quel que soit son parcours.
 */
export default {
  name: "SidebarSection",

  props: {
    learner: {
      type: Object,
      default: null,
    },
  },

  computed: {
    distinctions() {
      return (this.learner && this.learner.distinctions) || []
    },

    pointsLabel() {
      if (!this.learner) return ""
      if (!this.learner.nextLevelThreshold) return `${this.learner.points} points — niveau le plus élevé atteint`
      return `${this.learner.points} points sur ${this.learner.nextLevelThreshold}`
    },

    /**
     * Part parcourue **du niveau courant**, pas du total : mesurer depuis zéro afficherait une
     * barre presque pleine dès le premier palier franchi.
     */
    levelPercent() {
      if (!this.learner) return 0
      const floor = this.learner.levelThreshold || 0
      const ceiling = this.learner.nextLevelThreshold
      if (!ceiling || ceiling <= floor) return 100
      const share = ((this.learner.points - floor) / (ceiling - floor)) * 100
      return Math.max(0, Math.min(100, Math.round(share)))
    },
  },

  methods: {
    /**
     * État de la distinction, en clair. Une obtention révoquée ou échue n'est pas « obtenue » :
     * l'annoncer ainsi présenterait comme acquis ce qui a été retiré.
     */
    /**
     * Les deux formes cohabitent selon la source de la distinction : un nom `mdi-*` d'un côté, un
     * emoji de l'autre. Rendre l'un comme l'autre évite d'afficher un identifiant brut.
     */
    isIconName(icon) {
      return typeof icon === "string" && icon.startsWith("mdi-")
    },

    stateOf(distinction) {
      if (distinction.revoked) return "Retirée"
      if (distinction.isExpired) return "Échue"
      if (distinction.obtained) return "Obtenue"
      return "À obtenir"
    },
  },
}
</script>

<style scoped lang="scss">
@import "../../scss/nutriquest";

.progress-column {
  @include nq-card;
  padding: 1.25rem;
}

.column-title {
  color: $nq-ink;
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
}

.column-empty {
  color: $nq-ink-soft;
  font-style: italic;
  margin: 0;
}

.level-label {
  color: $nq-ink;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.points-line {
  color: $nq-ink-soft;
  font-size: 0.875rem;
  margin: 0 0 0.5rem 0;
}

.level-track {
  background: $nq-track;
  border-radius: 999px;
  height: 0.5rem;
  overflow: hidden;
}

.level-fill {
  background: $nq-fill;
  height: 100%;
}

.distinctions-title {
  color: $nq-ink;
  font-size: 1rem;
  font-weight: 600;
  margin: 1.25rem 0 0.5rem 0;
}

.distinctions {
  list-style: none;
  margin: 0;
  padding: 0;
}

.distinction {
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0;
}

.distinction + .distinction {
  border-top: 1px solid $nq-border;
}

/* Les badges de la maquette : pastille en relief. L'état est aussi dit en toutes lettres par
   `.distinction-state` — la couleur ne porte jamais l'information seule. */
.distinction-icon {
  align-items: center;
  background: $nq-accent-soft;
  border-radius: 10px;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 1.125rem;
  height: 2rem;
  justify-content: center;
  line-height: 1.2;
  width: 2rem;
}

.distinction-body {
  display: flex;
  flex-direction: column;
}

.distinction-label {
  color: $nq-ink;
  font-weight: 600;
}

.distinction-state {
  color: $nq-ink-soft;
  font-size: 0.8125rem;
}

.distinction-condition {
  color: $nq-muted;
  font-size: 0.8125rem;
}
</style>
